import React, { useEffect, useState } from 'react';

import { Button } from '../Button';
import { Icon } from '../Icon';
import { TextInput } from '../TextInput';
import { VisuallyHidden } from '../VisuallyHidden';
import { SvgCCPAIcon } from './SvgCCPAIcon';

import type {
  GlobalFooterProps,
  KeyValuePair,
  LinkGroupProps,
  LinkProps,
  NewsletterSignupProps,
  SecondaryProps,
  SimpleProps,
  SitemapProps,
} from './GlobalFooter.types';

const Link = ({ children, displayText, href }: LinkProps): JSX.Element => (
  <li>
    <a href={href}>{children ?? displayText}</a>
  </li>
);

const LinkGroup = ({ groupLabel, links }: LinkGroupProps): JSX.Element => (
  <li>
    <ul className='global-footer__sitemap-items'>
      {groupLabel && (
        <li>
          <h6 className='global-footer__sitemap-label'>{groupLabel}</h6>
        </li>
      )}
      {links?.map((link, index) => (
        <Link key={index} {...link} />
      ))}
    </ul>
  </li>
);

const NewsletterSignup = ({
  label = 'Newsletter sign up',
  inputPlaceholder = 'Email address',
  buttonLabel = 'Sign up',
  action,
  hiddenFields,
}: NewsletterSignupProps): JSX.Element => {
  const [urlHash, setUrlHash] = useState('');
  const [url, setUrl] = useState('');

  useEffect(() => {
    setUrl(
      window?.location?.href?.replace(window?.location?.hash, '') +
        '#newsletter-signup-footer-widget'
    );
    const path = window?.location?.href;
    const urlHash =
      path.indexOf('#') !== -1 ? path.slice(path.indexOf('#') + 1) : '';
    if (urlHash === 'newsletter-signup-footer-widget') {
      setUrlHash(urlHash);
    }
  }, []);

  return (
    <div className='global-footer__newsletter'>
      <form
        id='newsletter-signup-footer-widget'
        className='global-footer__newsletter-form'
        method='post'
        action={action}
      >
        {label && <label htmlFor='newsletter'>{label}</label>}
        <div className='global-footer__input-wrapper'>
          {hiddenFields?.map(({ key, value }: KeyValuePair, index) => {
            return (
              <input
                key={index}
                type='hidden'
                name={key?.trim()}
                value={value?.trim()}
              />
            );
          })}
          <input key='99' type='hidden' name='retURL' value={url} />

          {!urlHash && (
            <>
              <TextInput
                name={'email'}
                type={'email'}
                inputClassNames='global-footer__text-input'
                placeholder={inputPlaceholder}
                pattern="[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$"
                required
              />
              <Button
                buttonLabel={buttonLabel}
                buttonType='Outline'
                classNames='global-footer__button-dark'
                type={'submit'}
              />
            </>
          )}
          {urlHash && (
            <div
              className='global-footer__newsletter-message alert alert_info'
              role='alert'
            >
              <div className='alert__container'>
                <span className='alert__header'>You&apos;re subscribed!</span>
                <span className='alert__message'>Thanks for signing up.</span>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

const Sitemap = ({ linkGroups }: SitemapProps): JSX.Element =>
  linkGroups?.map ? (
    <ul className='global-footer__sitemap'>
      {linkGroups?.map((linkGroup, index) => (
        <LinkGroup key={index} {...linkGroup} />
      ))}
    </ul>
  ) : null;

const Secondary = ({
  socialLinks,
  navigationLinks,
  newsletterSignup,
}: SecondaryProps): JSX.Element => (
  <div className='global-footer__secondary-footer'>
    <div className='global-footer__logo-social-links'>
      <div className='global-footer__logo'>
        <a href='/' aria-label='Hagerty Logo'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
            width='106'
            height='22'
            viewBox='0 0 116 24'
          >
            <path
              fill='#101213'
              fillRule='evenodd'
              d='M115.8,1.3l-5.2,13.4v7.9h-6.2v-7.9L99.6,3.5h-3.9v19.1h-6.5V3.5h-5.3V1.3h21.2l4.3,10.3l3.9-10.3L115.8,1.3z M77.6,1.3c2.4,0,3.9,0.6,4.9,1.5c1.2,1.1,1.9,3,1.9,4.7c-0.1,2-0.8,3.6-3.8,5.3l5.4,9.7h-6.7l-4.7-8.9v8.9h-6.2V1.3L77.6,1.3z M75.8,4.1h-1.2v7.3h1.2c2.3,0,3.5-1.2,3.5-3.7C79.3,5.3,78.2,4.1,75.8,4.1z M59.5,19.9h8.2v2.7H53.3V1.4h13.8v2.7h-7.6v6.4h6.4v2.7 h-6.4L59.5,19.9z M44.2,10.6h8v10.9c0,0-0.4,0.1-1,0.3c-0.7,0.2-0.7,0.3-1.5,0.5c0,0-2.9,0.7-4.8,0.7c-3.2,0-5.6-0.9-7.3-2.8 c-1.7-1.9-2.6-4.5-2.6-8c0-3.3,0.9-6,2.8-8.1C39.5,2,42,1.1,44.9,1c3.6-0.1,7,1.2,7.1,6.4l-2.8,0l-0.1-1c-0.4-2.2-1.4-3.4-3.5-3.3 C44,3.3,43,3.8,42.5,4.9c-0.6,1.3-0.9,3.8-0.9,7.4c0,3.4,0.2,5.7,0.7,6.9c0.5,1.2,1.3,1.8,2.6,1.8c1.5,0,2.3-0.9,2.3-2.6v-5.2h-3.1 L44.2,10.6z M6.2,1.4v9.1h5V1.4h6.2v20l6.2-20h6.5l6.2,21.3h-6.5L28.5,18h-6.7l-1.4,4.6h-9.2v-9.5h-5v9.5H0V1.4L6.2,1.4z M25.3,6.5 l-2.8,9.4h5.5L25.3,6.5z M114,19c1,0,1.8,0.8,1.8,1.8c0,1-0.8,1.8-1.8,1.8c-1,0-1.8-0.8-1.8-1.8C112.2,19.8,113,19,114,19z M114,19.3c-0.8,0-1.5,0.7-1.5,1.5c0,0.8,0.7,1.5,1.5,1.5c0.8,0,1.5-0.7,1.5-1.5C115.5,20,114.8,19.3,114,19.3z M114.1,19.8 c0.5,0,0.7,0.2,0.7,0.6c0,0.4-0.2,0.5-0.5,0.6l0.5,0.9h-0.4l-0.5-0.8h-0.3v0.8h-0.4v-2.1L114.1,19.8z M114,20.1l-0.4,0v0.6h0.3 c0.2,0,0.5,0,0.5-0.3C114.4,20.1,114.2,20.1,114,20.1z'
            ></path>
          </svg>
        </a>
      </div>

      {socialLinks && (
        <div className='global-footer__social-icons'>
          {socialLinks?.map((socialLink, index) => {
            return (
              <Button
                key={index}
                href={socialLink?.href}
                buttonType={socialLink?.buttonType}
                buttonSize={socialLink?.buttonSize}
                buttonLabel={socialLink?.buttonLabel}
                hideText
              >
                <Icon {...socialLink?.icon} aria-hidden='true' />
              </Button>
            );
          })}
        </div>
      )}

      {navigationLinks && (
        <nav
          className='global-footer__nav-links'
          aria-label='Miscellaneous navigation'
        >
          <ul className='nav-items'>
            {navigationLinks?.map((navigationLink, index) => (
              <Link key={index} {...navigationLink} />
            ))}
          </ul>
        </nav>
      )}
    </div>

    {newsletterSignup && <NewsletterSignup {...newsletterSignup} />}
  </div>
);

const AgentFlags = ({ subdomain }: { subdomain: string }): JSX.Element => (
  <>
    <a
      className='global-footer__flag'
      href={`https://${subdomain}.hagertyagent.com`}
    >
      <VisuallyHidden>Hagerty Agents</VisuallyHidden>
      <Icon icon='FlagUsLegacy' size='large' aria-hidden='true' />
    </a>
    <a
      className='global-footer__flag'
      href={`https://${subdomain}.hagertybroker.ca`}
    >
      <VisuallyHidden>Hagerty Brokers</VisuallyHidden>
      <Icon icon='FlagCanadaLegacy' size='large' aria-hidden='true' />
    </a>
  </>
);

const DirectFlags = ({ subdomain }: { subdomain: string }): JSX.Element => (
  <>
    <a
      className='global-footer__flag'
      href={`https://${subdomain}.hagerty.com`}
    >
      <VisuallyHidden>Hagerty US</VisuallyHidden>
      <Icon icon='FlagUsLegacy' size='large' aria-hidden='true' />
    </a>
    <a className='global-footer__flag' href={`https://${subdomain}.hagerty.ca`}>
      <VisuallyHidden>Hagerty Canada</VisuallyHidden>
      <Icon icon='FlagCanadaLegacy' size='large' aria-hidden='true' />
    </a>
    <a className='global-footer__flag' href='https://www.hagerty.co.uk'>
      <VisuallyHidden>Hagerty UK</VisuallyHidden>
      <Icon icon='FlagUkLegacy' size='large' aria-hidden='true' />
    </a>
  </>
);

const Simple = ({
  navigationLinks,
  displayCCPALink = false,
  isAgent = false,
  subdomain = 'www',
}: SimpleProps): JSX.Element => (
  <div className='global-footer__simple-footer'>
    {navigationLinks && (
      <nav
        className='global-footer__utility-links'
        aria-label='Legal navigation'
      >
        <ul>
          {displayCCPALink && (
            <Link href='https://submit-irm.trustarc.com/services/validation/77672a09-1fe3-41a9-b911-46cc3603a305'>
              <SvgCCPAIcon />
              Your Privacy Choices
            </Link>
          )}
          {navigationLinks?.map((navigationLink, index) => (
            <Link key={index} {...navigationLink} />
          ))}
        </ul>
      </nav>
    )}
    <div className='global-footer__copyright-wrapper'>
      <div className='global-footer__flags'>
        {isAgent ? (
          <AgentFlags subdomain={subdomain} />
        ) : (
          <DirectFlags subdomain={subdomain} />
        )}
      </div>
      <div className='copyright-label'>
        &copy; 1996 &ndash; {new Date().getFullYear()} The Hagerty Group, LLC
      </div>
    </div>
  </div>
);

export const GlobalFooter = ({
  sitemap,
  secondary,
  simple,
  displayCCPALink = false,
  isAgent = false,
  subdomain = 'www',
}: GlobalFooterProps): JSX.Element => {
  return (
    <footer className='global-footer'>
      {sitemap && (
        <div className='container container_center'>
          <Sitemap {...sitemap} />
        </div>
      )}
      {secondary && (
        <>
          <div className='divider' />
          <div className='container container_center'>
            <Secondary {...secondary} />
          </div>
        </>
      )}
      {simple && (
        <>
          <div className='divider' />
          <div className='container container_center'>
            <Simple
              displayCCPALink={displayCCPALink}
              isAgent={isAgent}
              subdomain={subdomain}
              {...simple}
            />
          </div>
        </>
      )}
    </footer>
  );
};
